import React, { useEffect, useState, useRef } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import logoWhite from '../img/Logo-white.png';
import logo from '../img/logo/Logo.png';
import { useAuth } from '../hooks/appHooks';
import useAxiosFetchApi from '../hooks/useFetch';
import AppLoading from '../utils/AppLoading';
import { adminMenue, vendorMenue } from '../utils/NavigationMenue';
import LanguageSelector from '../utils/LanguageSelector';
import { useTranslation } from 'react-i18next';
import { CategoryProvider, OrderProvider, ProductProvider } from '../providers/AuthProvider';
import { IoLogOutOutline, IoMenu } from "react-icons/io5";
import useAxiosPrivate from '../apis/useAxiosPrivate';
import { Logout } from 'iconsax-reactjs';

const USER_PROFILE_URL = "/users/profile";
const LOGOUT_URL = "/users/logout";

const RequiredAuth = ({ routeRole }) => {
    const { auth, setAuth } = useAuth();
    const token = auth.token;
    const refreshToken = auth.refreshToken;
    const { t, i18n } = useTranslation();
    const axiosPrivate = useAxiosPrivate();
    const [state] = useAxiosFetchApi(USER_PROFILE_URL, {}, token);
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const sidebarRef = useRef(null); // <-- Add this line

    const userProfile = state?.data?.data?.user;
    let userRole = userProfile?.role || "";
    const dir = i18n.dir(); // 'ltr' or 'rtl'

    useEffect(() => {
        if (userProfile) {
            setAuth({ ...userProfile, token, refreshToken, role: userProfile.role });
        }
    }, [state?.data?.data, token, refreshToken, setAuth]);

    // Add this useEffect for outside click detection
    useEffect(() => {
        if (!sidebarOpen) return;

        function handleClickOutside(event) {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                setSidebarOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [sidebarOpen]);

    const handleUserLogout = async (e) => {
        e.preventDefault();
        try {
            await axiosPrivate.post(LOGOUT_URL, {}, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept-Language': i18n.language
                }
            });
            setAuth(null);
            localStorage.removeItem("session_token");
            navigate("/login", { replace: true });
        } catch (error) {
            console.error("Logout failed", error);
        }
    };

    const renderNavItems = (menu) =>
        menu.map(nav => (
            <NavLink
                key={nav.screenId}
                to={nav.route}
                className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2 rounded-md hover:bg-white/10 transition-colors duration-200 ${isActive ? 'bg-white/20 text-white font-semibold' : 'text-white'}`
                }
            >
                {nav.icon} {dir === 'rtl' ? nav.arTitle : nav.title}
            </NavLink>
        ));

    if (state.isLoading) return <AppLoading />;
    if (!userRole.includes(routeRole)) {
        return userProfile?.phone
            ? <Navigate to="/unauthorized" state={{ from: location }} replace />
            : <Navigate to="/login" state={{ from: location }} replace />;
    }

    return (
        <div className={`flex flex-col min-h-screen bg-gray-50 ${dir}`}>
            {/* Header */}
            <header className={`flex items-center justify-between px-6 py-4 shadow-md bg-white ${dir}`}>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setSidebarOpen(!sidebarOpen)}
                        className="md:hidden text-gray-600 hover:text-gray-900"
                    >
                        <IoMenu size={24} />
                    </button>
                    <div className="hidden md:flex items-center gap-2">
                        <img src={logo} alt="Logo" className="h-auto w-[100px]" />
                        {/* <span className="font-bold text-lg text-primary-color">Rateena</span> */}
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-gray-700">{userProfile?.email}</span>
                    <LanguageSelector />
                    <Logout size="32" className='text-red-500 cursor-pointer' variant="Bulk" onClick={handleUserLogout} />
                    {/* <button

                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                        {t("main.logoutNavBtn")}
                        <IoLogOutOutline size={20} />
                    </button> */}
                </div>
            </header>

            <div className="w-full flex flex-1">
                {/* Sidebar */}
                <aside
                    ref={sidebarRef} // <-- Add this prop
                    className={`
                        bg-[#06b4a0] text-white w-64 flex-shrink-0 flex-col px-6 pt-8
                        transition-transform duration-300 z-30
                        fixed md:static inset-y-0
                        ${dir === 'rtl' ? 'right-0' : 'left-0'}
                        ${sidebarOpen ? 'translate-x-0' : dir === 'rtl' ? 'translate-x-full md:translate-x-0' : '-translate-x-full md:translate-x-0'}
                    `}
                >
                    <div className="flex justify-center md:justify-start mb-[20px]">
                        <img src={logoWhite} alt="Rateena Logo" className="w-16 p-1" />
                    </div>
                    <nav className="space-y-2 flex flex-col">
                        {userRole.includes("VENDOR") &&
                            renderNavItems(
                                userRole === "VENDOR_USER"
                                    ? vendorMenue.filter(nav => nav.route !== "users")
                                    : vendorMenue
                            )
                        }
                        {userRole === "SUPERADMIN" &&
                            renderNavItems(adminMenue)
                        }
                    </nav>
                </aside>

                {/* Main Content */}
                <main className={`w-full flex-1 px-6 py-4 overflow-y-auto ${dir === 'rtl' ? 'mr-0 md:mr-16' : 'ml-0 md:ml-16'}`}>
                    <ProductProvider>
                        <OrderProvider>
                            <CategoryProvider>
                                <Outlet />
                            </CategoryProvider>
                        </OrderProvider>
                    </ProductProvider>
                </main>
            </div>
        </div>
    );
};

export default RequiredAuth;
